import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  CheckCircle, AlertCircle, Clock, Shield, FileText, Droplets, TrendingUp,
} from 'lucide-react';
import './ARTInteligente.css';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Motion';

const ARTInteligente = () => {
  return (
    <div className="art-page">
      <Header />

      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="art-hero">
        <div className="art-hero__aura" aria-hidden="true" />

        <div className="container">
          <Reveal className="art-hero__content">
            <h1>ART Inteligente</h1>
            <p className="art-hero__subtitle">
              Responsabilidade Técnica com Monitoramento 24/7 e Formação dos
              "Ninjas da Piscina"
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── Legislação ───────────────────────────────────────────────────── */}
      <section className="section legislacao">
        <div className="container">
          <Reveal className="section-head section-head--center">
            <h2 className="kicker">Resolução 335/23</h2>
            <h3 className="section-title">O que a legislação exige:</h3>
            <p className="section-lead">
              A legislação exige responsável técnico com emissão de ART para
              piscinas de uso coletivo. Isso significa que no cenário atual há:
            </p>
          </Reveal>

          <Stagger className="legislacao__grid" gap={0.09}>
            {[
              {
                icon: <AlertCircle size={26} />,
                tone: 'danger',
                title: 'Insegurança jurídica',
                text: 'Para os responsáveis técnicos',
              },
              {
                icon: <FileText size={26} />,
                tone: 'flame',
                title: 'Falta de padronização das análises de água',
                text: 'Cada responsável executa da forma que acha melhor',
              },
              {
                icon: <Shield size={26} />,
                tone: 'warning',
                title: 'Ausência de transparência para os banhistas',
                text: 'Banhistas sem acesso elevado sobre a água',
              },
              {
                icon: <TrendingUp size={26} />,
                tone: 'flame',
                title: 'Dados técnicos desorganizados e não armazenados',
                text: 'Blocos operacionais preenchidos e descartados com informações soltas',
              },
            ].map((card, i) => (
              <StaggerItem key={i} className="legislacao-card">
                <div className={`legislacao-card__icon is-${card.tone}`}>{card.icon}</div>
                <h4>{card.title}</h4>
                <p>{card.text}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ─── O que entrega ────────────────────────────────────────────────── */}
      <section className="section section--deep entregas">
        <div className="entregas__aura" aria-hidden="true" />

        <div className="container">
          <Reveal className="section-head section-head--center">
            <h2 className="kicker">Sobre Nós</h2>
            <h3 className="section-title">O Que a iPoolCare entrega</h3>
          </Reveal>

          <Stagger className="entregas__grid" gap={0.09}>
            {[
              {
                icon: <Shield size={28} />,
                title: 'Responsabilidade Técnica',
                text: 'Profissional qualificado responsável pela piscina',
              },
              {
                icon: <CheckCircle size={28} />,
                title: 'Supervisão Técnica Periódica',
                text: 'Fiscalização, controle e medidas corretivas',
              },
              {
                icon: <Droplets size={28} />,
                title: 'Análises de Água',
                text: 'Gestão técnica contínua verificando todos os parâmetros',
              },
              {
                icon: <Clock size={28} />,
                title: 'Integração',
                text: 'Monitoramento remoto 24/7 atuando nas anomalias',
              },
            ].map((card, i) => (
              <StaggerItem key={i} className="entrega-card">
                <div className="entrega-card__icon">{card.icon}</div>
                <h4>{card.title}</h4>
                <p>{card.text}</p>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal from="none" scale={0.94} className="entregas__banner">
            <p>
              Transformando soluções em <strong>Gestão Técnica Inteligente</strong>{' '}
              com responsabilidade formal e infraestrutura completa.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── O que está incluído ──────────────────────────────────────────── */}
      <section className="section section--sunk incluido">
        <div className="container">
          <Reveal className="section-head section-head--center">
            <h2 className="kicker">Proteção jurídica/sanitária e prevenção sempre</h2>
            <h3 className="section-title">O Que Está Incluído</h3>
          </Reveal>

          <Stagger className="incluido__list" gap={0.1} from="right">
            {[
              {
                title: 'Responsabilidade Técnica Formal',
                items: [
                  'Profissional técnico habilitado',
                  'Emissão da ART conforme resolução 335/23',
                  'Registro nos órgãos competentes',
                ],
              },
              {
                title: 'Monitoramento Remoto 24/7',
                items: [
                  'Vigilância contínua dos parâmetros',
                  'Alertas automáticos de não conformidades',
                  'Histórico completo e rastreável',
                  'Plataforma web de acesso aos dados',
                ],
              },
              {
                title: 'Gestão Técnica Contínua',
                items: [
                  'Análise constante da água',
                  'Ajuste e balanceamento químico',
                  'Controle de dosagens automatizado',
                  'Relatórios técnicos periódicos',
                ],
              },
              {
                title: 'Transparência Total',
                items: [
                  'Acesso aos dados pelos usuários',
                  'Monitores na área da piscina',
                  'Informações em tempo real',
                  'Compliance com vigilância sanitária',
                ],
              },
              {
                title: 'Formação dos "Ninjas da Piscina"',
                items: [
                  'Capacitação completa da equipe',
                  'Treinamento em manutenção e limpeza',
                  'Certificação dos profissionais',
                  'Suporte técnico contínuo',
                ],
              },
            ].map((block, i) => (
              <StaggerItem key={i} className="incluido-item">
                {/* Mesma numeração 1–5 que o markup anterior trazia escrita à mão. */}
                <div className="incluido-item__num">{i + 1}</div>
                <div className="incluido-item__body">
                  <h4>{block.title}</h4>
                  <ul>
                    {block.items.map((li, j) => (
                      <li key={j}>{li}</li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ─── Comparativo ──────────────────────────────────────────────────── */}
      <section className="section comparativo">
        <div className="container">
          <Reveal className="section-head section-head--center">
            <h2 className="section-title">COMPARATIVO ESTRATÉGICO</h2>
            <p className="section-lead">ART Tradicional vs iPoolCare ART Inteligente</p>
          </Reveal>

          <div className="comparativo__table">
            <Reveal className="comparativo__head">
              <div className="comparativo__col comparativo__col--tradicional">
                <h3>ART Tradicional</h3>
              </div>
              <div className="comparativo__col comparativo__col--inteligente">
                <h3>iPoolCare ART Inteligente</h3>
              </div>
            </Reveal>

            <Stagger gap={0.07}>
              {[
                [<><strong>Responsável técnico:</strong> Documento anual</>, <><strong>Responsabilidade técnica contínua:</strong> Gestão técnica contínua</>],
                [<><strong>Supervisão:</strong> Esporádica/passiva</>, <><strong>Monitoramento:</strong> 24/7</>],
                [<><strong>Análises:</strong> Sem supervisão remota</>, <><strong>Tecnologia IoT:</strong> Exclusiva</>],
                [<><strong>Treinamento:</strong> Básico operacional</>, <><strong>Formação dos "Ninjas":</strong> Treinamento técnico completo</>],
                [<><strong>Relatórios:</strong> Manuais</>, <><strong>Relatórios automáticos:</strong> Dados em tempo real</>],
                [<><strong>Sem supervisão remota</strong></>, <><strong>Preço acessível:</strong> Valor real</>],
                [<><strong>Não operacional elevada</strong></>, <><strong>Preço unitário:</strong></>],
              ].map(([tradicional, inteligente], i) => (
                <StaggerItem key={i} className="comparativo__row">
                  <div className="comparativo__cell comparativo__cell--tradicional">
                    <p>{tradicional}</p>
                  </div>
                  <div className="comparativo__cell comparativo__cell--inteligente">
                    <p>{inteligente}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          <Reveal from="none" scale={0.94} className="comparativo__footer">
            <p>
              <strong>
                A iPoolCare ART Inteligente protege juridicamente, sanitariamente
                e preventivamente.
              </strong>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── Requisitos e Planos ──────────────────────────────────────────── */}
      <section className="section section--sunk art-planos">
        <div className="container">
          <Reveal className="section-head section-head--center">
            <h2 className="section-title">Requisitos e Planos</h2>
            <p className="section-lead">Modelo de Precificação Mensal Recorrente</p>
          </Reveal>

          <Stagger className="art-planos__grid" gap={0.12}>
            {[
              {
                variant: 'small',
                title: 'Pequeno Porte',
                label: 'A partir de',
                price: 'R$ 2.000 - 3.000',
                perfil: ['Piscinas residenciais coletivas', 'Piscinas de pequenos hotéis', 'Clubes locais'],
                volume: 'Até 100m³',
              },
              {
                variant: 'medium',
                title: 'Médio Porte',
                label: 'Entre',
                price: 'R$ 3.000 - 10.000',
                perfil: ['Condomínios', 'Hotéis', 'Academias', 'Clubes recreativos'],
                volume: '100m³ a 500m³',
              },
              {
                variant: 'large',
                title: 'Grande Porte',
                label: 'Acima de',
                price: 'R$ 10.000+',
                perfil: ['Grandes complexos aquáticos', 'Resorts', 'Parques aquáticos', 'Múltiplas piscinas'],
                volume: 'Acima de 500m³ ou múltiplas unidades',
              },
            ].map((plano, i) => (
              <StaggerItem key={i} className="art-plano-card">
                <div className={`art-plano-card__head is-${plano.variant} on-color`}>
                  <h3>{plano.title}</h3>
                  <div className="art-plano-card__price">
                    <span className="art-plano-card__label">{plano.label}</span>
                    <span className="art-plano-card__value">{plano.price}</span>
                  </div>
                </div>

                <div className="art-plano-card__body">
                  <h4>Perfil da Instalação</h4>
                  <ul>
                    {plano.perfil.map((item, j) => <li key={j}>{item}</li>)}
                  </ul>
                  <h4>Volume típico</h4>
                  <p>{plano.volume}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="art-planos__note">
            <h4>Inclui em todos os planos:</h4>
            <ul>
              <li>✓ Emissão de ART conforme Resolução 335/23</li>
              <li>✓ Responsabilidade Técnica Formal</li>
              <li>✓ Monitoramento Remoto 24/7</li>
              <li>✓ Gestão Técnica Contínua</li>
              <li>✓ Formação dos "Ninjas"</li>
              <li>✓ Transparência aos usuários</li>
              <li>✓ Relatórios técnicos automatizados</li>
              <li>✓ Suporte técnico</li>
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────────────────── */}
      <section className="section art-cta">
        <div className="container">
          <Reveal from="none" scale={0.94} className="art-cta__panel">
            <h2>Vamos Trabalhar e Cuidar da sua Piscina?</h2>
            <p>
              Garanta segurança jurídica, economia operacional e água saudável
              sempre!
            </p>
            <a
              href="https://api.whatsapp.com/send?phone=5511982922298"
              className="btn btn--flame btn--lg"
              target="_blank"
              rel="noopener noreferrer"
            >
              Agende uma Avaliação
            </a>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ARTInteligente;
